import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import Select, { StylesConfig } from 'react-select';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import { AnyObject } from 'final-form';
import { ODataFilter, ODataOptions, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import PayPeriod from 'src/models/AP/PayPeriod';
import BillingSummaryReport from 'src/models/AP/BillingSummaryReport';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import CollapsibleSection from 'src/components/common/CollapsibleSection';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CommissionExcelReportDialog, { ICommissionExcelReportSubmit } from 'src/components/personnel/dialogs/CommissionExcelReportDialog';
import CommissionReportParamsDialog, { ICommissionReportParamsSubmit } from 'src/components/personnel/dialogs/CommissionReportParamsDialog';
import SectionUnitReportDialog from 'src/components/client-project/dialogs/SectionUnitReportDialog';

import { ArServiceContextProvider } from 'src/components/accounts-receivable/AccountsReceivableServiceContext';
import ArReportDialog from 'src/components/accounts-receivable/dialogs/ArReportDialog';
import CompanyExpensesReportDialog from 'src/components/accounts-receivable/dialogs/CompanyExpensesReportDialog';
import InvoiceCountyBreakdownReportDialog from 'src/components/accounts-receivable/dialogs/InvoiceCountyBreakdownReportDialog';
import LandRenewRevenueReportDialog from 'src/components/accounts-receivable/dialogs/LandRenewRevenueReportDialog';
import ClientsBilledReportDialog from 'src/components/accounts-receivable/dialogs/ClientsBilledReportDialog';
import VendorReportsDialog from 'src/components/accounts-payable/dialogs/VendorReportsDialog';
import VendorExcelExportDialog from 'src/components/accounts-payable/dialogs/VendorExcelExportDialog';
import OfficeExpenseReportOptionsDialog from 'src/components/accounts-payable/dialogs/OfficeExpenseReportOptionsDialog';
import APVoucherReportDialog from 'src/components/accounts-payable/dialogs/APVoucherReportDialog';
import ReportOptionsDialog from 'src/components/accounts-payable/dialogs/ReportOptionsDialog';
import GenerateInvoiceListingDialog from 'src/components/project-billing/dialogs/GenerateInvoiceListingDialog';
import GenerateTimesheetDialog from 'src/components/project-billing/dialogs/GenerateTimesheetDialog';
import GenerateTimeSummaryDialog from 'src/components/project-billing/dialogs/GenerateTimeSummaryDialog';
import GenerateExcelSummaryDialog from 'src/components/project-billing/dialogs/GenerateExcelSummaryDialog';
import GenerateSupplierLinkCsvDialog from 'src/components/project-billing/dialogs/GenerateSupplierLinkCsvDialog';
import GenerateExcelInvoiceListDialog from 'src/components/project-billing/dialogs/GenerateExcelInvoiceListDialog';
import GenerateBenefitsReportDialog from 'src/components/personnel/dialogs/benefits/GenerateBenefitsReportDialog';
import RevenueReportDialog from 'src/components/reports/dialogs/RevenueReportDialog';
import InvoiceContractorPayBreakdownReportDialog from 'src/components/reports/dialogs/InvoiceContractorPayBreakdownReportDialog';
import InvoiceOfficeBreakdownDialog from 'src/components/reports/dialogs/InvoiceOfficeBreakdownDialog';
import TimeRptDialog from 'src/components/reports/dialogs/TimeRptDialog';
import ContractorsBilledDialog from 'src/components/reports/dialogs/ContractorsBilledDialog';
import SectionBillingDialog from 'src/components/reports/dialogs/SectionBillingDialog';

const OFFICE_OPTION_VALUES = [
    { value: '1', label: 'ALL' },
    { value: '2', label: 'CO' },
    { value: '3', label: 'IH' },
    { value: '4', label: 'MD' },
    { value: '5', label: 'OK' },
    { value: '6', label: 'PA' },
    { value: '7', label: 'RN' },
    { value: '8', label: 'TX' },
];

// Ground-truthed against the ReportServer catalog (see FixProcedure_usp_BuildBillingSummary_
// AllOfficeFilter.sql's header note) - these are the only 4 of the 15 BillingSummary*-named SSRS
// reports independently runnable from a period+filters dialog. The other 11 are either subreports
// needing a REPORT_ID already staged by one of these 4, or a client/project drill-down report
// (BillingSummaryWorkV1) with a different, non-period-based parameter shape.
const BILLING_SUMMARY_REPORT_TYPES = [
    { value: 'BillingSummary', label: 'Billing Summary' },
    { value: 'BillingSummaryAPMPC', label: 'Billing Summary (APMPC)' },
    { value: 'BillingSummaryByProspect', label: 'Billing Summary by Prospect' },
    { value: 'BillingSummaryV1', label: 'Billing Summary (Legacy V1)' },
];

function insertCharAt(str: string, charToInsert: string, index: number) {
    return str.slice(0, index) + charToInsert + str.slice(index);
}

interface IReportLink {
    label: string;
    dialogName: DialogList;
}

interface IReportCategory {
    name: string;
    reports: Array<IReportLink>;
    comingSoon?: boolean;
}

const REPORT_CATEGORIES: Array<IReportCategory> = [
    {
        name: 'Accounts Receivable',
        reports: [
            { label: 'A/R Reports', dialogName: DialogList.AR_REPORT },
            { label: 'Employee Expenses', dialogName: DialogList.COMPANY_EXPENSES_REPORT },
            { label: 'Invoice Breakdown By County', dialogName: DialogList.INVOICE_COUNTY_BREAKDOWN_REPORT },
            { label: 'Land/Renewables Revenue', dialogName: DialogList.LAND_RENEW_REVENUE_REPORT },
            { label: 'Clients Billed', dialogName: DialogList.CLIENTS_BILLED_REPORT },
            { label: 'Renewable Clients Billed', dialogName: DialogList.CLIENTS_BILLED_RENEW_REPORT },
            { label: 'Western Land Clients Billed', dialogName: DialogList.CLIENTS_BILLED_WESTLAND_REPORT },
        ],
    },
    {
        name: 'Accounts Payable',
        reports: [
            { label: 'Vendor Reports', dialogName: DialogList.VENDOR_REPORTS },
            { label: 'Office Expense Reports', dialogName: DialogList.OFFICE_EXPENSE_REPORT_OPTIONS },
            { label: 'A/P Voucher Report', dialogName: DialogList.AP_VOUCHER_REPORT },
        ],
    },
    {
        name: 'Client | Project',
        reports: [
            { label: 'PM Margin Report', dialogName: DialogList.PROJECT_PM_MARGIN_REPORT },
            { label: 'Margins Report', dialogName: DialogList.PROJECT_MARGINS_REPORT },
            { label: 'WTexas Section', dialogName: DialogList.WTEXAS_SECTION_REPORT },
            { label: 'Eastern Section', dialogName: DialogList.EASTERN_SECTION_REPORT },
            { label: 'Section/Unit List', dialogName: DialogList.SECTION_UNIT_LIST_REPORT },
            { label: 'Revenue Reports', dialogName: DialogList.REVENUE_REPORT },
            { label: 'Invoice/Contractor Pay Breakdown Reports', dialogName: DialogList.INVOICE_CONTRACTOR_PAY_BREAKDOWN_REPORTS },
            { label: 'Section Billing Reports', dialogName: DialogList.SECTION_BILLING_REPORT },
        ],
    },
    {
        name: 'Personnel',
        reports: [
            { label: 'Generate Employee Benefits Report', dialogName: DialogList.GENERATE_BENEFITS_REPORT },
            { label: 'Contractors Report', dialogName: DialogList.CONTRACTORS_BILLED_REPORT },
        ],
    },
    {
        name: 'Billing',
        reports: [
            { label: 'Generate Invoice Listing', dialogName: DialogList.GENERATE_INVOICE_LISTING },
            { label: 'Generate Timesheet', dialogName: DialogList.GENERATE_TIMESHEET },
            { label: 'Generate Time Summary', dialogName: DialogList.GENERATE_TIME_SUMMARY },
            { label: 'Generate Excel Summary', dialogName: DialogList.GENERATE_EXCEL_SUMMARY },
            { label: 'Generate Excel Invoice List', dialogName: DialogList.GENERATE_EXCEL_INVOICE_LIST },
            { label: 'Generate SupplierLink CSV', dialogName: DialogList.GENERATE_SUPPLIER_LINK_CSV },
            { label: 'Build Invoice Office Breakdown', dialogName: DialogList.INVOICE_OFFICE_BREAKDOWN_REPORT },
            { label: 'Build Landman Time Report', dialogName: DialogList.TIME_RPT_REPORT },
            { label: 'Generate Billing Summary Report', dialogName: DialogList.GENERATE_BILLING_SUMMARY_REPORT },
        ],
    },
];

interface ICategoryOptionType {
    value: string;
    label: string;
}

// Static for now - just the category names, matching the initial "search finds a category" scope.
// A later pass can widen this to a flattened list of individual reports once the category-level
// search/jump is proven out.
const CATEGORY_OPTIONS: Array<ICategoryOptionType> = REPORT_CATEGORIES.map((category) => ({
    value: category.name,
    label: category.name,
}));

const searchSelectStyles: StylesConfig<ICategoryOptionType, false> = {
    control: (provided) => ({ ...provided, minHeight: '30px', height: '30px', padding: '0px' }),
    valueContainer: (provided) => ({ ...provided, padding: '0px 8px', height: '30px' }),
    input: (provided) => ({ ...provided, margin: '0px', padding: '0px', whiteSpace: 'nowrap' }),
    placeholder: (provided) => ({ ...provided, whiteSpace: 'nowrap' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '30px' }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '0px' }),
    clearIndicator: (provided) => ({ ...provided, padding: '0px' }),
    menu: (provided) => ({ ...provided, zIndex: 999 }),
};

const ReportDashboardPage = () => {
    const dispatch = useDispatch();
    // Static options array - react-select's own built-in isSearchable filtering handles narrowing
    // the list as the user types, so no debounced/local-filter state is needed here.
    const [selectedCategory, setSelectedCategory] = useState<ICategoryOptionType | null>(null);
    // Jumping to a category from the search box should land on it open even if the user (or a
    // future "start collapsed" default) had it closed - forceExpanded remounts CollapsibleSection
    // (see its own collapseKey) so re-finding the same category re-opens it every time, not just
    // the first.
    const [jumpToCategory, setJumpToCategory] = useState<{ name: string; nonce: number } | undefined>(undefined);
    const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
    // Same one-boolean-fed-to-every-CollapsibleSection's-forceExpanded pattern TeNonReconPage.tsx
    // uses for its own Expand All/Collapse All buttons.
    const [expandAllSections, setExpandAllSections] = useState<boolean>(true);
    const { get } = useFetch();
    const { post: postBinary, response: responseBinary } = useFetch('/v1.0', {
        responseType: 'arrayBuffer'
    });
    const [payPeriods, setPayPeriods] = useState<Array<PayPeriod>>(undefined);

    const showDialog = (dialog: DialogList) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const handleReportSubmit = (payload: AnyObject) => {
        console.log('Report requested', payload);
        hideDialog();
    };

    // Reuses CommissionPage.tsx's own submitExcelReport/GetReport-PMMarginReport pattern - this
    // dialog/endpoint already exists (CommissionPage's "Excel Report" toolbar button), just pointed
    // at from the dashboard too since it's a real, general-purpose PM margin report, not something
    // tied to a specific manager's own commission search.
    const { get: getPmMarginReport, response: pmMarginReportResponse } = useFetch();
    const submitPmMarginReport = async (params: ICommissionExcelReportSubmit) => {
        const query = new URLSearchParams({ begPeriod: String(params.begPeriod), endPeriod: String(params.endPeriod) });
        if (params.mgrNo) {
            query.set('mgrNo', String(params.mgrNo));
        }

        await getPmMarginReport(`/v1.0/reports/GetReport/PMMarginReport?${query.toString()}`);

        if (!pmMarginReportResponse.ok) {
            window.alert('Unable to generate the report. Please try again.');
            return;
        }

        if (pmMarginReportResponse.status === 204) {
            window.alert('No commission records matched those filters.');
            return;
        }

        const blob = await pmMarginReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'PMMarginReport.xlsx';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Reuses CommissionPage.tsx's own submitMarginsReport/MarginsByManager+MarginsByProject pattern
    // ('range' mode - Report By P/M, Beg/End Period, Client Number, Include Day Rate, no Manager
    // Number field).
    const { get: getMarginsReport, response: marginsReportResponse } = useFetch();
    const submitMarginsReport = async (params: ICommissionReportParamsSubmit) => {
        if (!params.begPeriod || !params.endPeriod) {
            window.alert('Please enter a beginning and ending period.');
            return;
        }

        const query = new URLSearchParams({ begPeriod: String(params.begPeriod), endPeriod: String(params.endPeriod), includeDayRate: String(!!params.includeDayRate) });
        if (params.clNo) {
            query.set('clNo', String(params.clNo));
        }

        const reportName = params.reportBy === 'M' ? 'MarginsByManager' : 'MarginsByProject';
        await getMarginsReport(`/v1.0/reports/GetReport/${reportName}?${query.toString()}`);

        if (!marginsReportResponse.ok) {
            window.alert('Unable to generate the report. Please try again.');
            return;
        }

        if (marginsReportResponse.status === 204) {
            window.alert('No commission records matched those filters.');
            return;
        }

        const blob = await marginsReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${reportName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Find button - jumps to whatever category is currently selected in the Select, forcing every
    // other category closed (expandAllSections: false) while jumpToCategory forces this one open -
    // same scroll-into-view + forced-open behavior the old auto-jump-on-select had, now paired with
    // collapsing the rest so the found category stands alone.
    const findSelectedCategory = () => {
        if (!selectedCategory) {
            return;
        }
        setExpandAllSections(false);
        setJumpToCategory({ name: selectedCategory.value, nonce: Date.now() });
        categoryRefs.current[selectedCategory.value]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const expandAllCategories = () => setExpandAllSections(true);
    const collapseAllCategories = () => setExpandAllSections(false);

    useEffect(() => {
        getPayPeriods();
    }, []);

    // Default (no searchTerm): same +/- 6 month window as ProjectBillingPage.tsx's
    // initializePayPeriodSearchSettings, so the Pay Period dropdown starts populated regardless of
    // which page opens the dialog. With a searchTerm: same PERIOD_NO Contains filter as
    // ProjectBillingPage.tsx's updatePayPeriodSearchOptions, reaching periods outside that window -
    // wired to ReportOptionsDialog's debounced onPayPeriodSearch below.
    const getPayPeriods = async (searchTerm?: string) => {
        const tableConfig: ITableConfig = {
            rowCount: 200,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        const searchOptions = ODataOptions.from(tableConfig);
        searchOptions.addOrderBy('Id', ODataOrderByDirection.Descending);

        const normalizedTerm = searchTerm ? Number(searchTerm.replace(/-/g, '')) : NaN;
        if (searchTerm && !isNaN(normalizedTerm)) {
            searchOptions.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, normalizedTerm));
        } else {
            const currentDate = new Date();
            const currentDate2 = new Date();
            const beginDate = new Date(currentDate.setMonth(currentDate.getMonth() - 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(currentDate2.setMonth(currentDate2.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            searchOptions.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThan, endDate));
            searchOptions.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThan, beginDate));
        }

        const queryString = searchOptions.toString();
        const result = await get(`/v1.0/AccountsPayable/payperiods${queryString}`);

        if (result?.items) {
            setPayPeriods(result.items.map((pp: PayPeriod) => {
                const insertIndex = pp.periodNumber.toString().length - 2;
                const ppString = insertCharAt(pp.periodNumber.toString(), '-', insertIndex);
                return { id: pp.id, name: ppString, beginDate: pp.beginDate, endDate: pp.endDate };
            }));
        }
    };

    const handlePayPeriodSearch = (searchTerm: string) => {
        getPayPeriods(searchTerm);
    };

    const handleBillingSummaryReport = async (data: AnyObject) => {
        const payload = new BillingSummaryReport();
        payload.reportVariant = data.reportType || 'BillingSummary';
        payload.employeeNumber = null;
        payload.clientNumber = data.clientNumber || null;
        payload.projectNumber = data.projectNumber || null;
        payload.subcode = data.sectionNumber || null;
        payload.manager = data.managerNumber || null;
        payload.extended = data.extended ? 'Y' : null;
        payload.exclude90 = null;
        payload.billingApproval = null;
        payload.workDescFormat = data.wdFormat || null;
        payload.crewChief = null;
        payload.ccNo = data.ccNo || null;
        payload.payPeriod = payPeriods?.find(pp => pp?.id?.toString() === data.payPeriod)?.name?.replace('-', '');
        payload.office = OFFICE_OPTION_VALUES.find(o => o?.value === data.offices)?.label;

        const result = await postBinary('/AccountsPayable/billing/report/summary', payload);

        if (!responseBinary.ok || result?.byteLength < 1) {
            return;
        }

        const blob = await responseBinary.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'BillingSummaryReport.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        hideDialog();
    };

    const displayCategories = () => {
        return REPORT_CATEGORIES.map((category) => {
            const isJumpTarget = jumpToCategory?.name === category.name;

            return (
            <div
                key={category.name}
                className="report-group-card"
                ref={(el) => { categoryRefs.current[category.name] = el; }}
            >
                <CollapsibleSection
                    key={isJumpTarget ? `${category.name}-jump-${jumpToCategory.nonce}` : `${category.name}-${expandAllSections}`}
                    title={category.name}
                    forceExpanded={isJumpTarget ? true : expandAllSections}
                >
                    <Page className="dlc-maxer-width">
                        <Page.Section width={12}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle={category.name}>
                                    <div className="report-group-report-list" style={{ border: 'solid rgb(99, 147, 193) 1px' }}>
                                        {category.comingSoon && <div className="report-category">Coming soon</div>}
                                        {!category.comingSoon && (
                                            <div className="report-category">
                                                {category.reports.map((report) => (
                                                    <div key={report.dialogName} style={{ display: 'flex' }}>
                                                        <div className="report clickable" onClick={() => showDialog(report.dialogName)}>
                                                            {report.label}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Panel>
                            </Panel>
                        </Page.Section>
                    </Page>
                </CollapsibleSection>
            </div>
            );
        });
    };

    return (
        <div style={{ margin: '1rem' }}>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    {/* Sized to its own content (field + buttons), not stretched to a fraction of
                        the page - matches TeNonReconSearch.tsx's own main bar. */}
                    <div style={{ width: 'fit-content', margin: '0 auto' }}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div style={{ display: 'flex', padding: '0', margin: '0', alignItems: 'center' }}>
                                <i
                                    className="fas fa-search"
                                    style={{ color: '#6393C1', position: 'relative', marginRight: '1rem', fontSize: '1em' }}
                                />
                                <div style={{ width: '20rem' }}>
                                    <Select
                                        name="reportCategorySearch"
                                        placeholder="Search Report Categories"
                                        options={CATEGORY_OPTIONS}
                                        value={selectedCategory}
                                        onChange={(option: ICategoryOptionType | null) => setSelectedCategory(option)}
                                        isClearable={true}
                                        isSearchable={true}
                                        styles={searchSelectStyles}
                                    />
                                </div>
                                <Button
                                    text="Find"
                                    className="blue-hover-button"
                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                    onClick={() => findSelectedCategory()}
                                />
                                <Button
                                    text="Expand All"
                                    className="blue-hover-button"
                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '10rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                    onClick={() => expandAllCategories()}
                                />
                                <Button
                                    text="Collapse All"
                                    className="blue-hover-button"
                                    style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '10rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                    onClick={() => collapseAllCategories()}
                                />
                            </div>
                        </Panel>
                    </Panel>
                    </div>
                </Page.Section>
            </Page>
            <div className="report-group-container">{displayCategories()}</div>

            <ArServiceContextProvider>
                <ArReportDialog
                    dialogName={DialogList.AR_REPORT}
                    hideDialog={hideDialog}
                    showDialog={showDialog}
                    handleReport={handleReportSubmit}
                />
            </ArServiceContextProvider>
            <CompanyExpensesReportDialog
                dialogName={DialogList.COMPANY_EXPENSES_REPORT}
                hideDialog={hideDialog}
            />
            <InvoiceCountyBreakdownReportDialog
                dialogName={DialogList.INVOICE_COUNTY_BREAKDOWN_REPORT}
                hideDialog={hideDialog}
            />
            <LandRenewRevenueReportDialog
                dialogName={DialogList.LAND_RENEW_REVENUE_REPORT}
                hideDialog={hideDialog}
            />
            <ClientsBilledReportDialog
                dialogName={DialogList.CLIENTS_BILLED_REPORT}
                hideDialog={hideDialog}
            />
            <ClientsBilledReportDialog
                dialogName={DialogList.CLIENTS_BILLED_RENEW_REPORT}
                hideDialog={hideDialog}
                dialogTitle="Renewable Clients Billed"
                fixedReportType="REN"
                defaultFilename="ClientsBilledRenew"
            />
            <ClientsBilledReportDialog
                dialogName={DialogList.CLIENTS_BILLED_WESTLAND_REPORT}
                hideDialog={hideDialog}
                dialogTitle="Western Land Clients Billed"
                fixedReportType="WL"
                defaultFilename="ClientsBilledWestLand"
            />
            <CommissionExcelReportDialog
                dialogName={DialogList.PROJECT_PM_MARGIN_REPORT}
                hideDialog={hideDialog}
                onSubmit={submitPmMarginReport}
            />
            <CommissionReportParamsDialog
                dialogName={DialogList.PROJECT_MARGINS_REPORT}
                hideDialog={hideDialog}
                mode="range"
                onSubmit={submitMarginsReport}
            />
            <SectionUnitReportDialog
                dialogName={DialogList.WTEXAS_SECTION_REPORT}
                hideDialog={hideDialog}
                dialogTitle="WTexas Section"
                reportType="WTX"
                defaultFilename="WTexasSection"
            />
            <SectionUnitReportDialog
                dialogName={DialogList.EASTERN_SECTION_REPORT}
                hideDialog={hideDialog}
                dialogTitle="Eastern Section"
                reportType="EASTERN"
                defaultFilename="EasternSection"
            />
            <SectionUnitReportDialog
                dialogName={DialogList.SECTION_UNIT_LIST_REPORT}
                hideDialog={hideDialog}
                dialogTitle="Section/Unit List"
                reportType="UNITLIST"
                defaultFilename="SectionUnitList"
            />
            <VendorReportsDialog
                dialogName={DialogList.VENDOR_REPORTS}
                hideDialog={hideDialog}
                showDialog={showDialog}
            />
            <VendorExcelExportDialog
                dialogName={DialogList.VENDOR_EXCEL_EXPORT}
                hideDialog={hideDialog}
            />
            <OfficeExpenseReportOptionsDialog
                dialogName={DialogList.OFFICE_EXPENSE_REPORT_OPTIONS}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleOfficeExpenseReport={handleReportSubmit}
            />
            <APVoucherReportDialog
                dialogName={DialogList.AP_VOUCHER_REPORT}
                hideDialog={hideDialog}
                hideCurrentVoucherOption={true}
            />
            <GenerateInvoiceListingDialog
                dialogName={DialogList.GENERATE_INVOICE_LISTING}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateTimesheetDialog
                dialogName={DialogList.GENERATE_TIMESHEET}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateTimeSummaryDialog
                dialogName={DialogList.GENERATE_TIME_SUMMARY}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateExcelSummaryDialog
                dialogName={DialogList.GENERATE_EXCEL_SUMMARY}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateExcelInvoiceListDialog
                dialogName={DialogList.GENERATE_EXCEL_INVOICE_LIST}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateSupplierLinkCsvDialog
                dialogName={DialogList.GENERATE_SUPPLIER_LINK_CSV}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleSubmit={handleReportSubmit}
            />
            <GenerateBenefitsReportDialog
                dialogName={DialogList.GENERATE_BENEFITS_REPORT}
                hideDialog={hideDialog}
                benefitYears={[]}
                selectedTsdNo={0}
                currentYear={new Date().getFullYear()}
                onSubmit={(year, active, tsdNo) => {
                    console.log('Benefits report requested', { year, active, tsdNo });
                }}
            />
            <RevenueReportDialog
                dialogName={DialogList.REVENUE_REPORT}
                hideDialog={hideDialog}
                handleReport={handleReportSubmit}
            />
            <InvoiceContractorPayBreakdownReportDialog
                dialogName={DialogList.INVOICE_CONTRACTOR_PAY_BREAKDOWN_REPORTS}
                hideDialog={hideDialog}
            />
            <InvoiceOfficeBreakdownDialog
                dialogName={DialogList.INVOICE_OFFICE_BREAKDOWN_REPORT}
                hideDialog={hideDialog}
                handleReport={handleReportSubmit}
            />
            <TimeRptDialog
                dialogName={DialogList.TIME_RPT_REPORT}
                hideDialog={hideDialog}
                handleReport={handleReportSubmit}
            />
            <ContractorsBilledDialog
                dialogName={DialogList.CONTRACTORS_BILLED_REPORT}
                hideDialog={hideDialog}
                handleReport={handleReportSubmit}
            />
            <SectionBillingDialog
                dialogName={DialogList.SECTION_BILLING_REPORT}
                hideDialog={hideDialog}
                handleReport={handleReportSubmit}
            />
            <ReportOptionsDialog
                dialogName={DialogList.GENERATE_BILLING_SUMMARY_REPORT}
                hideDialog={hideDialog}
                showDialog={showDialog}
                handleBillingReport={handleBillingSummaryReport}
                onPayPeriodSearch={handlePayPeriodSearch}
                parentType={3}
                payPeriods={payPeriods?.map(p => ({
                    label: !!p?.name ? `(${p.name})` : '',
                    value: `${p?.id}`
                }))}
                offices={OFFICE_OPTION_VALUES}
                reportTypes={BILLING_SUMMARY_REPORT_TYPES}
            />
        </div>
    );
};

export default ReportDashboardPage;



// WEBPACK FOOTER //
// ./src/components/reports/ReportDashboardPage.tsx